/**
 * ConnectorRow — single row in the existing-connectors list.
 * Click the row to open the config dialog in edit mode.
 */

import { useTranslation } from 'react-i18next';

import type { Connector } from '@/api/types';
import { useLocaleStore } from '@/stores/localeStore';
import { formatRelativeTime } from '@/utils/format';

import { ConnectorIcon } from './ConnectorIcon';
import { ConnectorStatusChip } from './ConnectorStatusChip';

export function ConnectorRow({
  connector,
  onEdit,
}: {
  connector: Connector;
  onEdit: (id: string) => void;
}) {
  const { t } = useTranslation('connectors');
  const locale = useLocaleStore((s) => s.locale);

  const linkedCount = connector.linked_gi_ids.length;
  const tested = connector.last_tested_at
    ? formatRelativeTime(connector.last_tested_at, locale)
    : t('list.never_tested');

  return (
    <button
      type="button"
      onClick={() => onEdit(connector.id)}
      className="flex w-full items-center gap-4 rounded-lg border border-border bg-surface p-4 text-start transition-colors hover:border-border-strong hover:bg-surface-raised"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-spark-soft text-spark">
        <ConnectorIcon type={connector.type} className="h-5 w-5" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-sm font-semibold text-ink">{connector.name}</h3>
          <ConnectorStatusChip status={connector.status} />
        </div>
        <p className="mt-0.5 truncate font-mono text-xs text-ink-muted">
          {connector.config.endpoint || t('list.no_endpoint')}
        </p>
      </div>

      <div className="hidden shrink-0 flex-col items-end gap-0.5 text-xs text-ink-subtle sm:flex">
        <span>{t('list.linked', { count: linkedCount })}</span>
        <span>{t('list.last_tested', { when: tested })}</span>
      </div>
    </button>
  );
}
